import type { ThemeTokenFamily } from '@personalizehq/editor';
import type { ButtonVariant, Corners, FontToken, Gradient, Shadow, Theme } from '@personalizehq/schemas/ir';

export function summarizeThemeToken(family: ThemeTokenFamily, value: unknown, theme: Theme): string {
  if (family === 'colors') {
    const color = value as Theme['colors'][string];
    return `${color.light} · ${color.dark}`;
  }
  if (family === 'fonts') {
    const font = value as FontToken;
    // WHAT THE TOKEN ACTUALLY IS. The inspector shows `body` and an `Aa` sample and stops, so an
    // author picking a font token is choosing a name, not a typeface. This is the line that makes it
    // a typeface again — and it is all derived, so it can never drift from the token.
    //
    // The line height is shown RESOLVED (points), not as the stored ratio: `1.5` tells you nothing
    // about whether two lines will fit, and the solver's own law is `round(size × line_height)`
    // (SPEC §6.4), so this is the number that decides the height.
    const parts = [
      font.style === 'italic' ? `${font.family} Italic` : font.family,
      `${font.size}/${Math.round(font.size * font.line_height)}`,
      String(font.weight),
    ];
    // Tracking is signed on purpose — "+0.5" and "−0.5" are different design decisions and a bare
    // "0.5" hides which one you made.
    if (font.letter_spacing) parts.push(`${font.letter_spacing > 0 ? '+' : '−'}${Math.abs(font.letter_spacing)}`);
    // The Dynamic Type ceiling. Nothing else in the panel says it, and it is the field that decides
    // whether the design survives a user who needs large text.
    parts.push(`≤${font.max_scale}×`);
    return parts.join(' · ');
  }
  if (family === 'radii') {
    const radius = value as number | Corners;
    return typeof radius === 'number' ? `${radius}px` : `${radius.tl} · ${radius.tr} · ${radius.br} · ${radius.bl}px`;
  }
  if (family === 'elevations') {
    const shadow = value as Shadow;
    return `${shadow.x ?? 0}, ${shadow.y}, ${shadow.blur}, ${shadow.spread ?? 0} · ${shadow.color}`;
  }
  if (family === 'gradients') {
    const gradient = value as Gradient;
    return `${gradient.angle}° · ${gradient.stops.length} stops`;
  }
  const button = value as ButtonVariant;
  const background = button.bg === null ? 'transparent' : typeof button.bg === 'string' ? button.bg : button.bg.gradient;
  const radius = typeof button.radius === 'string' ? button.radius : typeof button.radius === 'number' ? `${button.radius}px` : 'per-corner';
  return `${background} · ${radius}${button.elevation && theme.elevations[button.elevation] ? ` · ${button.elevation}` : ''}`;
}
